/* @refresh reload */
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { LanguageProvider } from './contexts/LanguageContext'
import { DialogProvider } from './contexts/DialogContext'
import {
  EXTERNAL_REQUEST_FEEDBACK_END_EVENT,
  EXTERNAL_REQUEST_FEEDBACK_START_EVENT,
  EXTERNAL_REQUEST_SCOPE_ATTR,
  classifyExternalRequest,
  isExternalRequestProfile,
  type ExternalRequestFeedbackEventDetail,
} from './lib/externalRequestFeedback'
import { clearAuthSession } from './lib/utils'
import './index.css'

type AppRoot = ReturnType<typeof ReactDOM.createRoot>
type AppWindow = typeof window & {
  __YIJIANMAI_APP_ROOT__?: AppRoot
  __YIJIANMAI_ROOT_ELEMENT__?: HTMLElement
  __YIJIANMAI_ROOT_OBSERVER__?: MutationObserver
  __YIJIANMAI_ROOT_DEBUGGER_INSTALLED__?: boolean
  __YIJIANMAI_BOOT_COUNT__?: number
  __YIJIANMAI_FETCH_AUTH_GUARD_INSTALLED__?: boolean
  __YIJIANMAI_AUTH_REDIRECT_LOCK_UNTIL__?: number
  __YIJIANMAI_REQUEST_FEEDBACK_LAYER__?: HTMLElement | null
}

const ROOT_MARKER = 'data-ozon-app-root'
const SHELL_MARKER = 'data-yijianmai-app-shell'
const THEME_STORAGE_KEY = 'ui:theme'
const LEGACY_THEME_STORAGE_KEY = 'theme'
const MANDATORY_BROWSER_REFRESH_STORAGE_KEY = 'mandatory-browser-refresh-token'
const MANDATORY_BROWSER_REFRESH_TOKEN = '2026-05-11-frontend-polling-throttle-v1'
const AUTH_REDIRECT_THROTTLE_MS = 2000
const AUTH_ERROR_CODES = new Set(['UNAUTHORIZED', 'TOKEN_EXPIRED', 'TOKEN_REVOKED', 'INVALID_TOKEN', 'USER_NOT_FOUND'])
const AUTH_IGNORE_PATH_PREFIXES = ['/api/v1/auth/login', '/api/v1/auth/sms', '/api/v1/auth/turnstile', '/api/v1/auth/password']
const REQUEST_FEEDBACK_SILENT_PATH_PREFIXES = ['/api/v1/notifications/unread-count', '/api/v1/announcements/unread-count']
const REQUEST_INTERACTION_TTL_MS = 1200
const REQUEST_FEEDBACK_LAYER_ID = 'network-request-feedback-layer'
const REQUEST_LOADING_ATTR = 'data-request-loading'
const REQUEST_SOURCE_ATTR = 'data-request-source'
const REQUEST_INTERACTION_SELECTOR = [
  'button',
  '[role="button"]',
  'a',
  '[role="link"]',
  'input[type="submit"]',
  'input[type="button"]',
  '[data-request-action]',
  '[data-network-request]'
].join(',')
let externalRequestSequence = 0

type RequestSourceType = 'button' | 'clickable' | 'global'

type RequestInteractionSnapshot = {
  element: HTMLElement | null
  sourceType: RequestSourceType
  at: number
}

type RootErrorBoundaryState = {
  hasError: boolean
  error?: Error | null
}

class RootErrorBoundary extends React.Component<{ children: React.ReactNode }, RootErrorBoundaryState> {
  state: RootErrorBoundaryState = {
    hasError: false,
    error: null,
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[RootErrorBoundary]', error, errorInfo)
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    const isChinese = typeof navigator !== 'undefined' ? navigator.language.toLowerCase().startsWith('zh') : true
    const title = isChinese ? '页面发生错误' : 'Unexpected error'
    const subtitle = isChinese
      ? '你可以刷新页面或返回任务中心继续。'
      : 'You can refresh the page or return to Task Center to continue.'
    const errorName = this.state.error?.name || 'Error'
    const errorMessage = this.state.error?.message || (isChinese ? '未知错误' : 'Unknown error')

    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-xl rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)]">
          <div className="text-sm font-black text-slate-900">{title}</div>
          <div className="mt-1 text-xs text-slate-500">{subtitle}</div>

          <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <div className="text-xs font-bold text-slate-800">{errorName}</div>
            <pre className="mt-3 whitespace-pre-wrap break-words text-[11px] text-slate-600 leading-relaxed">{errorMessage}</pre>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                window.location.href = '/task-center'
              }}
              className="rounded-xl bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-700"
            >
              {isChinese ? '返回任务中心' : 'Back to Task Center'}
            </button>
            <button
              type="button"
              onClick={() => {
                window.location.href = '/workspace'
              }}
              className="rounded-xl bg-white px-4 py-2 text-xs font-bold text-slate-700 border border-slate-200 hover:bg-slate-50"
            >
              {isChinese ? '返回工作台' : 'Back to Workspace'}
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-xl bg-white px-4 py-2 text-xs font-bold text-slate-700 border border-slate-200 hover:bg-slate-50"
            >
              {isChinese ? '刷新' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>
    )
  }
}

const applyThemePreference = () => {
  if (typeof document === 'undefined') {
    return
  }
  try {
    const stored = window.localStorage?.getItem(THEME_STORAGE_KEY) || window.localStorage?.getItem(LEGACY_THEME_STORAGE_KEY)
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches
    const shouldUseDark = stored ? stored === 'dark' : Boolean(prefersDark)
    document.documentElement.classList.toggle('dark', shouldUseDark)
  } catch (error) {
    // ignore theme errors
  }
}

const applyMandatoryBrowserRefresh = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    const previousToken = window.localStorage?.getItem(MANDATORY_BROWSER_REFRESH_STORAGE_KEY)
    if (previousToken === MANDATORY_BROWSER_REFRESH_TOKEN) {
      return false
    }
    window.localStorage?.setItem(MANDATORY_BROWSER_REFRESH_STORAGE_KEY, MANDATORY_BROWSER_REFRESH_TOKEN)
    window.setTimeout(() => {
      window.location.reload()
    }, 0)
    return true
  } catch (error) {
    return false
  }
}

const resolveRequestUrl = (input: RequestInfo | URL) => {
  if (typeof input === 'string') {
    return input
  }
  if (input instanceof URL) {
    return input.toString()
  }
  return input.url
}

const resolveRequestMethod = (input: RequestInfo | URL, init?: RequestInit) => {
  if (init?.method) {
    return String(init.method).trim().toUpperCase() || 'GET'
  }
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return String(input.method || 'GET').trim().toUpperCase() || 'GET'
  }
  return 'GET'
}

const resolveAuthorizationHeader = (input: RequestInfo | URL, init?: RequestInit) => {
  if (init?.headers) {
    return new Headers(init.headers).get('Authorization')
  }
  if (typeof Request !== 'undefined' && input instanceof Request) {
    return input.headers.get('Authorization')
  }
  return null
}

const extractBearerToken = (authorization: string | null) => {
  if (typeof authorization !== 'string') {
    return ''
  }
  const normalized = authorization.trim()
  if (!normalized.toLowerCase().startsWith('bearer ')) {
    return ''
  }
  return normalized.slice(7).trim()
}

const resolveCurrentAuthToken = () => {
  try {
    const userToken = String(window.localStorage.getItem('token') || '').trim()
    if (userToken) {
      return userToken
    }
    return String(window.localStorage.getItem('guest_token') || '').trim()
  } catch {
    return ''
  }
}

const extractAuthErrorCode = async (response: Response) => {
  const payload = await response.clone().json().catch(() => null)
  if (!payload || typeof payload !== 'object') {
    return ''
  }
  const maybeCode = (payload as { code?: unknown }).code
  if (typeof maybeCode !== 'string') {
    return ''
  }
  return maybeCode.trim().toUpperCase()
}

const shouldSkipUnauthorizedRedirect = (requestUrl: string) => {
  try {
    const url = new URL(requestUrl, window.location.origin)
    return AUTH_IGNORE_PATH_PREFIXES.some((prefix) => url.pathname.startsWith(prefix))
  } catch {
    return AUTH_IGNORE_PATH_PREFIXES.some((prefix) => requestUrl.includes(prefix))
  }
}

const shouldSuppressGlobalRequestFeedback = (requestUrl: string) => {
  try {
    const url = new URL(requestUrl, window.location.origin)
    return REQUEST_FEEDBACK_SILENT_PATH_PREFIXES.some((prefix) => url.pathname.startsWith(prefix))
  } catch {
    return REQUEST_FEEDBACK_SILENT_PATH_PREFIXES.some((prefix) => requestUrl.includes(prefix))
  }
}

const maybeTriggerAuthExpiredRedirect = async (
  appWindow: AppWindow,
  requestUrl: string,
  response: Response,
  hadBearerToken: boolean,
  requestBearerToken: string
) => {
  if (!hadBearerToken || response.status !== 401) {
    return
  }
  if (shouldSkipUnauthorizedRedirect(requestUrl)) {
    return
  }
  const code = await extractAuthErrorCode(response)
  if (code && !AUTH_ERROR_CODES.has(code)) {
    return
  }

  const now = Date.now()
  const lockUntil = appWindow.__YIJIANMAI_AUTH_REDIRECT_LOCK_UNTIL__ ?? 0
  if (lockUntil > now) {
    return
  }

  const activeToken = resolveCurrentAuthToken()
  if (requestBearerToken && activeToken && requestBearerToken !== activeToken) {
    return
  }

  appWindow.__YIJIANMAI_AUTH_REDIRECT_LOCK_UNTIL__ = now + AUTH_REDIRECT_THROTTLE_MS

  clearAuthSession()
  window.dispatchEvent(
    new CustomEvent('auth:unauthorized', {
      detail: { code: code || 'UNAUTHORIZED', status: response.status, path: requestUrl },
    })
  )
}

const ensureRequestFeedbackLayer = (appWindow: AppWindow) => {
  const cachedLayer = appWindow.__YIJIANMAI_REQUEST_FEEDBACK_LAYER__
  if (cachedLayer && document.body.contains(cachedLayer)) {
    return cachedLayer
  }
  const existing = document.getElementById(REQUEST_FEEDBACK_LAYER_ID)
  if (existing instanceof HTMLElement) {
    appWindow.__YIJIANMAI_REQUEST_FEEDBACK_LAYER__ = existing
    return existing
  }

  const layer = document.createElement('div')
  layer.id = REQUEST_FEEDBACK_LAYER_ID
  layer.className = 'global-request-feedback-layer'
  layer.setAttribute('aria-hidden', 'true')
  layer.innerHTML = `
    <div class="global-request-feedback__bar"></div>
    <div class="global-request-feedback__badge">
      <span class="global-request-feedback__dot"></span>
      <span class="global-request-feedback__dot"></span>
      <span class="global-request-feedback__dot"></span>
    </div>
  `
  document.body.appendChild(layer)
  appWindow.__YIJIANMAI_REQUEST_FEEDBACK_LAYER__ = layer
  return layer
}

const resolvePointerInteractiveElement = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return null
  }
  let node: HTMLElement | null = target
  while (node && node !== document.body) {
    if (node.getAttribute('aria-disabled') !== 'true') {
      const style = window.getComputedStyle(node)
      if (style.cursor === 'pointer') {
        return node
      }
    }
    node = node.parentElement
  }
  return null
}

const resolveInteractiveElement = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return null
  }
  const element = target.closest(REQUEST_INTERACTION_SELECTOR)
  if (element instanceof HTMLElement) {
    return element
  }
  return resolvePointerInteractiveElement(target)
}

const resolveRequestSourceType = (element: HTMLElement | null): RequestSourceType => {
  if (!element) {
    return 'global'
  }
  if (element.matches('button, [role="button"], input[type="submit"], input[type="button"]')) {
    return 'button'
  }
  return 'clickable'
}

const resolveExternalRequestScopeId = (element: HTMLElement | null): string => {
  if (!element) {
    return ''
  }
  const scope = element.closest('[' + EXTERNAL_REQUEST_SCOPE_ATTR + ']')
  if (!(scope instanceof HTMLElement)) {
    return ''
  }
  return String(scope.getAttribute(EXTERNAL_REQUEST_SCOPE_ATTR) || '').trim()
}

const installUnauthorizedResponseGuard = (appWindow: AppWindow) => {
  if (appWindow.__YIJIANMAI_FETCH_AUTH_GUARD_INSTALLED__) {
    return
  }
  if (typeof window.fetch !== 'function') {
    return
  }
  appWindow.__YIJIANMAI_FETCH_AUTH_GUARD_INSTALLED__ = true
  const nativeFetch = window.fetch.bind(window)
  const pendingRequestsByElement = new WeakMap<HTMLElement, number>()
  const feedbackLayer = ensureRequestFeedbackLayer(appWindow)
  let activeRequestCount = 0
  let activeGlobalRequestCount = 0
  let lastInteraction: RequestInteractionSnapshot | null = null

  const syncRequestFeedbackLayer = () => {
    feedbackLayer.dataset.active = activeRequestCount > 0 ? 'true' : 'false'
    feedbackLayer.dataset.global = activeGlobalRequestCount > 0 ? 'true' : 'false'
    feedbackLayer.dataset.count = String(activeRequestCount)
    feedbackLayer.style.setProperty('--request-count', String(activeRequestCount))
  }

  const setElementRequestLoading = (element: HTMLElement | null, sourceType: RequestSourceType, direction: 1 | -1) => {
    if (!element) {
      return
    }
    const previousCount = pendingRequestsByElement.get(element) ?? 0
    const nextCount = Math.max(0, previousCount + direction)

    if (nextCount === 0) {
      pendingRequestsByElement.delete(element)
      element.removeAttribute(REQUEST_LOADING_ATTR)
      element.removeAttribute(REQUEST_SOURCE_ATTR)
      element.removeAttribute('aria-busy')
      return
    }

    pendingRequestsByElement.set(element, nextCount)
    element.setAttribute(REQUEST_LOADING_ATTR, 'true')
    element.setAttribute(REQUEST_SOURCE_ATTR, sourceType)
    element.setAttribute('aria-busy', 'true')
  }

  const recordInteraction = (target: EventTarget | null) => {
    const element = resolveInteractiveElement(target)
    if (!element) {
      return
    }
    lastInteraction = {
      element,
      sourceType: resolveRequestSourceType(element),
      at: Date.now(),
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      recordInteraction(event.target)
    },
    true
  )

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return
      }
      recordInteraction(event.target)
    },
    true
  )

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = (event as SubmitEvent).submitter
      if (submitter instanceof HTMLElement) {
        recordInteraction(submitter)
        return
      }
      recordInteraction(event.target)
    },
    true
  )

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const requestUrl = resolveRequestUrl(input)
    const requestMethod = resolveRequestMethod(input, init)
    const authorization = resolveAuthorizationHeader(input, init)
    const requestBearerToken = extractBearerToken(authorization)
    const hadBearerToken = Boolean(requestBearerToken)
    const now = Date.now()
    const shouldBindInteraction =
      Boolean(lastInteraction?.element)
      && Boolean(lastInteraction?.element && document.contains(lastInteraction.element))
      && Boolean(lastInteraction?.at && now - lastInteraction.at <= REQUEST_INTERACTION_TTL_MS)
    const requestSourceType: RequestSourceType = shouldBindInteraction
      ? (lastInteraction?.sourceType || 'button')
      : 'global'
    const requestElement = shouldBindInteraction ? lastInteraction?.element : null
    const shouldTrackRequestFeedback = shouldBindInteraction || !shouldSuppressGlobalRequestFeedback(requestUrl)
    const externalProfile = classifyExternalRequest(requestUrl, init ? { ...init, method: requestMethod } : { method: requestMethod })
    const externalScopeId = requestElement ? resolveExternalRequestScopeId(requestElement) : ''
    const shouldTrackExternalRequest = isExternalRequestProfile(externalProfile) && Boolean(externalScopeId)
    const externalRequestDetail: ExternalRequestFeedbackEventDetail | null = shouldTrackExternalRequest && externalProfile
      ? {
        requestId: 'external-' + Date.now() + '-' + (++externalRequestSequence),
        requestUrl,
        method: requestMethod,
        scopeId: externalScopeId,
        startedAt: now,
        profile: externalProfile,
      }
      : null

    if (shouldTrackRequestFeedback) {
      activeRequestCount += 1
      if (requestElement) {
        setElementRequestLoading(requestElement, requestSourceType, 1)
      } else {
        activeGlobalRequestCount += 1
      }
      syncRequestFeedbackLayer()
    }
    if (externalRequestDetail) {
      window.dispatchEvent(new CustomEvent(EXTERNAL_REQUEST_FEEDBACK_START_EVENT, { detail: externalRequestDetail }))
    }

    try {
      const response = await nativeFetch(input, init)
      void maybeTriggerAuthExpiredRedirect(appWindow, requestUrl, response, hadBearerToken, requestBearerToken)
      return response
    } finally {
      if (shouldTrackRequestFeedback) {
        activeRequestCount = Math.max(0, activeRequestCount - 1)
        if (requestElement) {
          setElementRequestLoading(requestElement, requestSourceType, -1)
        } else {
          activeGlobalRequestCount = Math.max(0, activeGlobalRequestCount - 1)
        }
        syncRequestFeedbackLayer()
      }
      if (externalRequestDetail) {
        window.dispatchEvent(new CustomEvent(EXTERNAL_REQUEST_FEEDBACK_END_EVENT, { detail: externalRequestDetail }))
      }
    }
  }
}

const isRootDebugEnabled = () => {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.has('debugRoot')) {
      return true
    }
    return window.localStorage?.getItem('debug:root') === '1'
  } catch (error) {
    return false
  }
}

const nodeContainsRoot = (node: Node) => {
  if (!(node instanceof Element)) {
    return false
  }
  if (node.id === 'root') {
    return true
  }
  if (node.getAttribute(ROOT_MARKER) === 'true') {
    return true
  }
  if (node.getAttribute(SHELL_MARKER) === 'true') {
    return true
  }
  if (node.childElementCount === 0) {
    return false
  }
  return node.querySelector(`#root, [${ROOT_MARKER}="true"], [${SHELL_MARKER}="true"]`) !== null
}

const nodeIntroducesDuplicateRoot = (node: Element) => {
  const introducesShell =
    node.getAttribute(SHELL_MARKER) === 'true' || node.querySelector(`[${SHELL_MARKER}="true"]`) !== null
  if (introducesShell) {
    const existingShells = document.querySelectorAll(`[${SHELL_MARKER}="true"]`).length
    if (existingShells > 0) {
      return true
    }
  }

  const introducesRoot = node.id === 'root' || node.querySelector('#root') !== null
  if (introducesRoot) {
    const existingRoots = document.querySelectorAll('#root').length
    if (existingRoots > 0) {
      return true
    }
  }

  return false
}

const installRootInjectionDebugger = (appWindow: AppWindow) => {
  if (!isRootDebugEnabled()) {
    return
  }
  if (appWindow.__YIJIANMAI_ROOT_DEBUGGER_INSTALLED__) {
    return
  }
  appWindow.__YIJIANMAI_ROOT_DEBUGGER_INSTALLED__ = true

  const originalAppendChild = Node.prototype.appendChild
  const originalInsertBefore = Node.prototype.insertBefore
  const originalReplaceChild = Node.prototype.replaceChild
  const nodeProto = Node.prototype as any

  const logIfNeeded = (node: Node) => {
    if (!nodeContainsRoot(node)) {
      return
    }
    if (!(node instanceof Element)) {
      return
    }
    if (!nodeIntroducesDuplicateRoot(node)) {
      return
    }
    console.error('[RootDebug] 检测到重复注入（新增了 #root 或 app-shell）', node, new Error().stack)
  }

  nodeProto.appendChild = function appendChildPatched(this: Node, newChild: Node) {
    logIfNeeded(newChild)
    return originalAppendChild.call(this, newChild)
  }

  nodeProto.insertBefore = function insertBeforePatched(this: Node, newChild: Node, refChild: Node | null) {
    logIfNeeded(newChild)
    return originalInsertBefore.call(this, newChild, refChild)
  }

  nodeProto.replaceChild = function replaceChildPatched(this: Node, newChild: Node, oldChild: Node) {
    logIfNeeded(newChild)
    return originalReplaceChild.call(this, newChild, oldChild)
  }
}

const pickRootElement = () => {
  const candidates = Array.from(document.querySelectorAll('#root')) as HTMLElement[]
  if (candidates.length === 0) {
    return null
  }
  const marked = candidates.find((node) => node.getAttribute(ROOT_MARKER) === 'true')
  return marked ?? candidates[0]
}

const cleanupDuplicateRoots = (primaryRoot: HTMLElement) => {
  const roots = Array.from(document.querySelectorAll('#root')) as HTMLElement[]
  roots.forEach((node) => {
    if (node !== primaryRoot) {
      node.remove()
    }
  })
}

const cleanupDuplicateShells = (primaryRoot: HTMLElement) => {
  const shells = Array.from(document.querySelectorAll(`[${SHELL_MARKER}="true"]`)) as HTMLElement[]
  if (shells.length <= 1) {
    return
  }
  const primaryShell = shells.find((shell) => primaryRoot.contains(shell)) ?? shells[0]
  shells.forEach((shell) => {
    if (shell !== primaryShell) {
      shell.remove()
    }
  })
}

const appWindow = window as AppWindow

if (!applyMandatoryBrowserRefresh()) {
  const rootElement = pickRootElement()
  if (!rootElement) throw new Error('Failed to find the root element')

  rootElement.setAttribute(ROOT_MARKER, 'true')
  cleanupDuplicateRoots(rootElement)
  cleanupDuplicateShells(rootElement)
  applyThemePreference()
  appWindow.__YIJIANMAI_ROOT_ELEMENT__ = rootElement
  installUnauthorizedResponseGuard(appWindow)

  appWindow.__YIJIANMAI_BOOT_COUNT__ = (appWindow.__YIJIANMAI_BOOT_COUNT__ ?? 0) + 1
  if (isRootDebugEnabled() && appWindow.__YIJIANMAI_BOOT_COUNT__ > 1) {
    console.error('[RootDebug] main.tsx 被重复执行，可能存在多次注入/多次 HMR 评估', new Error().stack)
  }

  installRootInjectionDebugger(appWindow)

  if (!appWindow.__YIJIANMAI_ROOT_OBSERVER__) {
    let cleanupScheduled = false
    const scheduleRootCleanup = () => {
      if (cleanupScheduled) {
        return
      }
      cleanupScheduled = true
      window.setTimeout(() => {
        cleanupScheduled = false
        cleanupDuplicateRoots(rootElement)
        cleanupDuplicateShells(rootElement)
      }, 32)
    }

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList') {
          continue
        }
        for (const node of mutation.addedNodes) {
          if (nodeContainsRoot(node)) {
            scheduleRootCleanup()
            return
          }
        }
      }
    })
    observer.observe(document.body, { childList: true })
    appWindow.__YIJIANMAI_ROOT_OBSERVER__ = observer
  }

  const App = () => {
    const [appRouter, setAppRouter] = useState<React.ComponentProps<typeof RouterProvider>['router'] | null>(null)

    useEffect(() => {
      let mounted = true
      import('./router').then((module) => {
        if (mounted) {
          setAppRouter(module.router)
        }
      })
      return () => {
        mounted = false
      }
    }, [])

    if (!appRouter) {
      return null
    }

    return <RouterProvider router={appRouter} />
  }

  const appRoot = appWindow.__YIJIANMAI_APP_ROOT__ ?? ReactDOM.createRoot(rootElement)
  appWindow.__YIJIANMAI_APP_ROOT__ = appRoot

  appRoot.render(
    <React.StrictMode>
      <LanguageProvider>
        <DialogProvider>
          <div {...{ [SHELL_MARKER]: 'true' }}>
            <RootErrorBoundary>
              <App />
            </RootErrorBoundary>
          </div>
        </DialogProvider>
      </LanguageProvider>
    </React.StrictMode>
  )
}
